import type { AppState } from '@protector/entities/__legacy__/redux.types';
import { Entitlements } from '@protector/entities/licensing/constants';
import { getLicensingStatus, getSelectedCompanyId, getSelectedPropertyId } from 'core/core.selectors';
import { validatePlansAndEntitlements } from 'core/utils/functions';
import { useCompanyOrOrganizationMatch, useScoutingScoreMatch } from 'core/utils/hooks/usePathMatches';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import useFieldClusteringConfigPermission from 'pages/field-clustering/configuration/hooks/useFieldClusteringConfigPermission';
import { useGetRBACAuthorityUser } from 'querys/rbac/rbac.query';
import { RBACActions, RBACLevels, RBACPermissionTypes } from '@protector/entities/rbac/constants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router';
import SVGFieldClusterGlyph from 'st-protector-icons/Glyph/FieldClusterGlyph';
import SVGMethodologyGlyph from 'st-protector-icons/Glyph/MethodologyGlyph';
import SVGScoutingScoreGlyph from 'st-protector-icons/Glyph/ScoutingScoreGlyph';
import type { STOrgSettingsCardProps } from '../components/settings/org-settings-card.component';

export const useFarmSettingsOptions = () => {
  const [t] = useTranslation();
  const segmentTracking = useSegmentTracking();
  const navigate = useNavigate();
  const scoutingScoreMatch = useScoutingScoreMatch();
  const showScoutingScore = useSelector((state: AppState) => state.uiState.auth.permissionFlags?.showScoutingScore?.value);
  const selectedCompany = useSelector(getSelectedCompanyId);
  const selectedProperty = useSelector(getSelectedPropertyId);
  const licensingStatus = useSelector(getLicensingStatus);
  const showFieldClusterConfig = useFieldClusteringConfigPermission();
  const hallOfPropertiesMatch = useCompanyOrOrganizationMatch();

  const orgSettingsMatch = useMatch({
    path: '/company/:companyId/edit-methodology',
    end: false
  });

  const propertyMatch = useMatch({
    path: '/company/:companyId/property/:propertyId',
    end: false
  });

  const methodologyPermissions = useGetRBACAuthorityUser({
    rbacActions: RBACActions.PANEL_METHODOLOGY,
    rbacLevels: RBACLevels.ORG,
    rbacPermission: [RBACPermissionTypes.READ, RBACPermissionTypes.WRITE]
  });

  const showOrgMethodologyConfig = useMemo(() => {
    const companyAndProperty = !!selectedCompany && !!selectedProperty;
    const isMethodologyVisible = !!methodologyPermissions?.length;
    const hideByEntitlement = !!validatePlansAndEntitlements(licensingStatus, null, [Entitlements.HIDE_METHODOLOGY_CONFIGURATION]);

    return !hideByEntitlement && companyAndProperty && isMethodologyVisible;
  }, [licensingStatus, selectedCompany, selectedProperty, methodologyPermissions]);

  const onHandleRedirectTo = (event: string, pathRedirect: string) => () => {
    segmentTracking.track(event);
    navigate(pathRedirect);
  };

  const handleFieldClusterModal = () => {
    navigate(`/company/${selectedCompany}/property/${selectedProperty}/field-clustering`);
  };

  const getOrgSettingContentOptions = (): STOrgSettingsCardProps[] => {
    const editMethodologyPath = selectedCompany ? `/company/${selectedCompany}/edit-methodology` : '/';

    return [
      {
        isAvailable: showOrgMethodologyConfig,
        svgIcon: <SVGMethodologyGlyph />,
        title: t('modals.organization_settings.methodology_card.title'),
        description: t('modals.organization_settings.methodology_card.description'),
        onClick: onHandleRedirectTo('Settings - Tapped to access Methodology', editMethodologyPath)
      },
      {
        isAvailable: showFieldClusterConfig,
        svgIcon: <SVGFieldClusterGlyph />,
        title: t('modals.organization_settings.field_cluster_card.title'),
        description: t('modals.organization_settings.field_cluster_card.description'),
        onClick: handleFieldClusterModal
      }
    ];
  };

  const getPropertySettingContentOptions = (): STOrgSettingsCardProps[] => {
    const scoutingScorePath = `/company/${selectedCompany}/property/${selectedProperty}/scouting-score`;
    return [
      {
        isAvailable: showScoutingScore,
        svgIcon: <SVGScoutingScoreGlyph className='settings-menu-icon' />,
        title: t('modals.organization_settings.scouting_score_card.title'),
        description: t('modals.organization_settings.scouting_score_card.description'),
        onClick: onHandleRedirectTo('Settings - Tapped to access Scouting Score', scoutingScorePath)
      }
    ];
  };

  const orgSettingsContentOptions = getOrgSettingContentOptions().filter(option => option?.isAvailable);
  const propertySettingsContentOptions = getPropertySettingContentOptions().filter(option => option?.isAvailable);

  const propertySettingsAvailable = !!propertySettingsContentOptions.length && !!propertyMatch && !scoutingScoreMatch;
  const orgSettingsButtonAvailable =
    !!hallOfPropertiesMatch && !orgSettingsMatch && !!orgSettingsContentOptions.length && !propertySettingsAvailable;

  return {
    orgSettingsContentOptions,
    propertySettingsContentOptions,
    orgSettingsButtonAvailable,
    propertySettingsAvailable
  };
};
